import { Calendar } from '@mantine/dates';
import { Clear } from '@mui/icons-material';
import { Box, FormHelperText, Grid, SxProps, TextField, Typography, styled } from '@mui/material';
import * as Sentry from '@sentry/react';
import moment, { Moment } from 'moment-timezone';
import {
    ChangeEventHandler,
    FocusEventHandler,
    KeyboardEventHandler,
    forwardRef,
    useEffect,
} from 'react';
import DatePicker from 'react-datepicker';

import { AXLE_TEAL, DARK_AXLE_TEAL, LIGHT_AXLE_TEAL_BACKGROUND } from '../../constants/colors';
import { VisitAvailabilityMetricsByDate } from '../../fetching/api/availability';
import {
    guessTimezoneIfNull,
    startOfCurrentHour,
    timezoneAdjustedMoment,
} from '../../utils/datetimes/misc';
import { generateTimeIntervals } from '../../utils/datetimes/timeIntervals';
import { InputState, useTransformedInputState } from '../../utils/inputState';
import { useIsDown } from '../../utils/mediaBreakpoints';
import { ReturnComponent, Style } from '../../utils/reactTypes';
import { AxleIconButton } from '../buttons/AxleIconButton';
import { AxleDatePickerHeader, axleDatePickerPopupSx } from '../dates/axleDatePickerStyles';
import { If } from '../utils/conditionalComponents';
import { AxleAutocompleteField } from './AxleAutocompleteField';
import { FieldWithAxleLabel } from './AxleLabel';

const DateItem = styled('div')(() => ({
    display: 'flex',
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
}));

const getBackgroundColorForAvailability = (availability: number): string => {
    // Apply a scaling factor on opacity
    const opacity = 0.9 * availability;

    // Calculate brightness adjustment (0 to 255) - less availability means lighter and closer white
    const brightnessAdjust = Math.round(((1 - availability) * 255) / 6);

    // Mix the base color (102, 193, 191) with white based on availability
    const r = Math.min(102 + brightnessAdjust, 255);
    const g = Math.min(193 + brightnessAdjust, 255);
    const b = Math.min(191 + brightnessAdjust, 255);

    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

const AvailabilityIndicator = styled('div')(
    ({ availability, isSelected }: { availability: number; isSelected: boolean }) => ({
        width: isSelected ? '100%' : '90%',
        height: '6px',
        backgroundColor: getBackgroundColorForAvailability(availability),
        position: 'absolute',
        bottom: 0,
        left: isSelected ? '0%' : '5%',
    }),
);

// Manages the hour and minute on the passed in Moment object.
export const AxleMomentTimeOfDayField = ({
    label = undefined,
    enabled = true,
    inputState,
    hideHelperText = false,
    timezone: timezoneRaw,
    nullable = false,
    inlineLabel = false,
    intervalMinutes = 15,
    defaultDateIfStateWasNull = () => startOfCurrentHour(),
    displayMissingOptionValue = false,
    _autocompleteRootInputStyles,
}: {
    label?: string | undefined;
    enabled?: boolean;
    inputState: InputState<Moment | null>;
    hideHelperText?: boolean;
    timezone: string | null;
    nullable?: boolean;
    inlineLabel?: boolean;
    intervalMinutes?: number;
    defaultDateIfStateWasNull?: () => Moment;
    displayMissingOptionValue?: boolean;
    _autocompleteRootInputStyles?: Style;
}): ReturnComponent => {
    const timezone = guessTimezoneIfNull(timezoneRaw);

    const timeOfDayState: InputState<string | null> = useTransformedInputState({
        inputState,
        transform: (datetime: Moment | null): string | null => {
            return datetime !== null ? datetime.tz(timezone).format('HH:mm:SS') : null;
        },
        untransform: (newTimeOfDay: string | null): Moment | null => {
            if (newTimeOfDay === null) {
                if (!nullable) {
                    Sentry.captureMessage(
                        "Tried to set time of day to null, but it's not nullable.",
                    );
                }
                return null;
            }

            const oldDatetime = inputState.value ?? defaultDateIfStateWasNull();
            const newDatetime = moment.tz(
                oldDatetime.tz(timezone).format('YYYY-MM-DD') + 'T' + newTimeOfDay,
                'YYYY-MM-DDTHHmm',
                timezone,
            );

            return newDatetime;
        },
    });

    return (
        <AxleAutocompleteField
            label={label}
            inlineLabel={inlineLabel}
            enabled={enabled}
            hideHelperText={hideHelperText}
            inputState={timeOfDayState}
            disableClearable={!nullable}
            options={generateTimeIntervals(intervalMinutes)}
            optionToValue={(timeOfDay: string) => timeOfDay}
            optionToLabel={(timeOfDay: string) => moment(timeOfDay, 'HHmm').format('LT')}
            sortOptions={false}
            displayMissingOptionValue={displayMissingOptionValue}
            _autocompleteRootInputStyles={_autocompleteRootInputStyles}
        />
    );
};

export const AxleDateTimeOfDayField = ({
    label = undefined,
    enabled = true,
    inputState,
    hideHelperText = false,
    timezone: timezoneRaw,
    nullable = false,
    intervalMinutes = 15,
    defaultDateIfStateWasNull = () => startOfCurrentHour().toDate(),
}: {
    label?: string | undefined;
    enabled?: boolean;
    inputState: InputState<Date | null>;
    hideHelperText?: boolean;
    timezone: string | null;
    nullable?: boolean;
    intervalMinutes?: number;
    defaultDateIfStateWasNull?: () => Date;
}): ReturnComponent => {
    const timezone = guessTimezoneIfNull(timezoneRaw);

    const timeOfDayState: InputState<string | null> = useTransformedInputState({
        inputState,
        transform: (datetime: Date | null): string | null => {
            return datetime !== null ? moment(datetime).tz(timezone).format('HH:mm:SS') : null;
        },
        untransform: (newTimeOfDay: string | null): Date | null => {
            if (newTimeOfDay === null) {
                if (!nullable) {
                    Sentry.captureMessage(
                        "Tried to set time of day to null, but it's not nullable.",
                    );
                }
                return null;
            }

            const oldDatetime = inputState.value ?? defaultDateIfStateWasNull();
            const newDatetime = moment
                .tz(
                    moment(oldDatetime).tz(timezone).format('YYYY-MM-DD') + 'T' + newTimeOfDay,
                    'YYYY-MM-DDTHHmm',
                    timezone,
                )
                .toDate();

            return newDatetime;
        },
    });

    return (
        <AxleAutocompleteField
            label={label}
            enabled={enabled}
            hideHelperText={hideHelperText}
            inputState={timeOfDayState}
            disableClearable={!nullable}
            options={generateTimeIntervals(intervalMinutes)}
            optionToValue={(timeOfDay: string) => timeOfDay}
            optionToLabel={(timeOfDay: string) => moment(timeOfDay, 'HHmm').format('LT')}
            sortOptions={false}
        />
    );
};

// This is a custom component we pass in to react-datepicker.
//  It does not affect the popup that react-datepicker opens to let the user pick the date.
//  Its job is to render `value`, trigger `onClick` when clicked, and forward the
//  keyboard/change/focus/blur handlers that react-datepicker supplies so that the
//  user can type a date directly into the field.
// We need the forwardRef to allow react-datepicker to access the underlying
//  DOM input element (I think because react-datepicker tries to call focus() on it).
const AxleDateFieldCustomDisplayField = forwardRef(
    (
        {
            // Props used by react-datepicker.
            value,
            onClick,
            onChange,
            onFocus,
            onBlur,
            onKeyDown,
            // Extra props we pass in.
            enabled,
            nullable,
            onClear,
        }: {
            value: string | null | undefined;
            onClick?: () => void;
            onChange?: ChangeEventHandler<HTMLInputElement>;
            onFocus?: FocusEventHandler<HTMLInputElement>;
            onBlur?: FocusEventHandler<HTMLInputElement>;
            onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
            enabled: boolean;
            nullable: boolean;
            onClear: () => void;
        },
        ref,
    ) => (
        <TextField
            inputRef={ref}
            //  Formatting for the value happens in the AxleDateField Date Picker's on change.
            //   if you try to format here, it will double apply the timezone translation and will
            //   cause the date to be wrong.
            value={value ?? ''}
            placeholder={'Click to select…'}
            disabled={!enabled}
            onClick={onClick}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            variant='outlined'
            fullWidth
            size='small'
            sx={{
                input: {
                    // This style is to match <AxleAutocompleteField/> text when it has
                    //  a null value, as this field is often side-by-side by <AxleTimeField/>
                    //  which uses an <AxleAutocompleteField/> inside it with a clearable option.
                    color: value ? undefined : 'gray',
                },
            }}
            InputProps={{
                endAdornment:
                    nullable && value ? (
                        <AxleIconButton
                            icon={<Clear />}
                            tooltip={'Clear Date'}
                            onClick={onClear}
                            // This style is to match the clear button on <AxleTimeField/>,
                            //  which uses an <AxleAutocompleteField/> inside it with a clearable option.
                            iconButtonStyle={{
                                padding: '0',
                                '& svg': { fontSize: '1.25rem !important' },
                            }}
                        />
                    ) : undefined,
            }}
        />
    ),
);
AxleDateFieldCustomDisplayField.displayName = 'AxleDateFieldCustomDisplayField';

// Manages the date on the passed in Moment object
export const AxleMomentField = ({
    label = undefined,
    enabled = true,
    hideHelperText = false,
    inputState,
    timezone: timezoneRaw,
    nullable = false,
    minSelectableDateTime,
    maxSelectedableDateTime,
    defaultTimeIfStateWasNull = () => startOfCurrentHour(),
}: {
    label?: string | undefined;
    enabled: boolean;
    hideHelperText?: boolean;
    inputState: InputState<Moment | null>;
    timezone: string | null;
    nullable?: boolean;
    minSelectableDateTime?: Moment | null;
    maxSelectedableDateTime?: Moment | null;
    defaultTimeIfStateWasNull?: () => Moment;
}): ReturnComponent => {
    const timezone = guessTimezoneIfNull(timezoneRaw);
    const isMobileLayout = useIsDown('sm');

    // noinspection RequiredAttributes
    return (
        <Box
            sx={{
                '& .react-datepicker-wrapper': {
                    // This makes the field fullwidth.
                    width: '100%',
                },
                ...axleDatePickerPopupSx,
                '& .react-datepicker__day--selected': {
                    backgroundColor: AXLE_TEAL,
                },
            }}
        >
            <FieldWithAxleLabel label={label} required={inputState.required} enabled={enabled}>
                <DatePicker
                    renderCustomHeader={({ date, decreaseMonth, increaseMonth }) => (
                        <AxleDatePickerHeader
                            date={date}
                            decreaseMonth={decreaseMonth}
                            increaseMonth={increaseMonth}
                        />
                    )}
                    // BEHAVIOUR:
                    minDate={minSelectableDateTime?.toDate()}
                    maxDate={maxSelectedableDateTime?.toDate()}
                    selected={
                        inputState.value !== null
                            ? inputState.value.clone().local(true).toDate()
                            : null
                    }
                    onChange={(newDate) => {
                        if (newDate === null) {
                            if (nullable) inputState.setInputState(null);
                            else
                                Sentry.captureMessage(
                                    "Tried to set date to null, but it's not nullable.",
                                );
                            return;
                        }

                        // If the state was null, and the user is setting the time of day,
                        //  use `defaultTimeIfStateWasNull()` to determine the time of the day.
                        const oldDatetime = inputState.value ?? defaultTimeIfStateWasNull();
                        const newDatetime = moment.tz(
                            moment(newDate).tz(timezone, true).format('YYYY-MM-DD') +
                                'T' +
                                oldDatetime.format('HHmm'),
                            'YYYY-MM-DDTHHmm',
                            timezone,
                        );
                        inputState.setInputState(newDatetime);
                    }}
                    disabled={!enabled}
                    // MOBILE LAYOUT:
                    withPortal={isMobileLayout}
                    // STYLING:
                    // The arrow often shows up in the wrong place, so just don’t show it.
                    showPopperArrow={false}
                    // This affects where the popper shows up if there is not enough space,
                    //  either above or below. And by forcing it to always show below, at least
                    //  the user can try to scroll down to reveal the UX that is cut off. Scrolling
                    //  up doesn’t seem to work as well.
                    popperPlacement='top-start'
                    // CUSTOM COMPONENT:
                    customInput={
                        // @ts-expect-error The remaining props are passed in by react-datepicker.
                        <AxleDateFieldCustomDisplayField
                            enabled={enabled}
                            nullable={nullable}
                            onClear={() => inputState.setInputState(null)}
                        />
                    }
                />
                <If condition={!hideHelperText}>
                    <FormHelperText error={inputState.showError}>
                        {inputState.helperText}
                    </FormHelperText>
                </If>
            </FieldWithAxleLabel>
        </Box>
    );
};

// Manage a whole datetime field.
export const AxleMomentTimeField = ({
    label,
    enabled = true,
    inputState,
    nullable,
    timezone,
    intervalMinutes = 15,
    displayMissingOptionValue = false,
}: {
    label: string;
    enabled?: boolean;
    inputState: InputState<Moment | null>;
    nullable: boolean;
    timezone: string | null;
    intervalMinutes?: number;
    displayMissingOptionValue?: boolean;
}): ReturnComponent => {
    return (
        <FieldWithAxleLabel label={label} required={inputState.required}>
            <Box>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <AxleMomentField
                            enabled={enabled}
                            hideHelperText={true}
                            inputState={inputState}
                            nullable={nullable}
                            timezone={timezone}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <AxleMomentTimeOfDayField
                            enabled={enabled}
                            hideHelperText={true}
                            inputState={inputState}
                            // We don't make the time of day nullable, because
                            //  1. there is already going to be an "X" button on the date field
                            //      to clear the date field
                            //  2. the default behaviour of clearing the time of day field is to
                            //      reset the field to its initial default value, which may not
                            //      be the same as null, and that's confusing.
                            nullable={false}
                            timezone={timezone}
                            intervalMinutes={intervalMinutes}
                            displayMissingOptionValue={displayMissingOptionValue}
                        />
                    </Grid>
                </Grid>
            </Box>
            <FormHelperText error={inputState.showError}>{inputState.helperText}</FormHelperText>
        </FieldWithAxleLabel>
    );
};

// keys are classnames, values are dates to apply them to
type HighlightDates = Record<string, Date[]>;
export interface IAxleDateFieldProps {
    label?: string | undefined;
    enabled: boolean;
    hideHelperText?: boolean;
    inputState: InputState<Date | null>;
    nullable?: boolean;
    minSelectableDateTime?: Date | null;
    maxSelectableDateTime?: Date | null;
    defaultTimeIfStateWasNull?: () => Date;
    disabledKeyboardNavigation?: boolean;
    filterDate?: (date: Date) => boolean;
    highlightDates?: (Date | HighlightDates)[];
    sx?: SxProps;
}

export const AxleDateField = ({
    label = undefined,
    enabled = true,
    hideHelperText = false,
    inputState,
    nullable = false,
    minSelectableDateTime,
    maxSelectableDateTime,
    disabledKeyboardNavigation = false,
    filterDate,
    highlightDates,
    sx,
}: IAxleDateFieldProps): ReturnComponent => {
    const isMobileLayout = useIsDown('sm');

    // noinspection RequiredAttributes
    return (
        <Box
            sx={{
                '& .react-datepicker-wrapper': {
                    // This makes the field fullwidth.
                    width: '100%',
                },
                ...axleDatePickerPopupSx,
                '& .react-datepicker__day--selected': {
                    backgroundColor: AXLE_TEAL,
                },
                '& .react-datepicker__day--highlighted': {
                    backgroundColor: LIGHT_AXLE_TEAL_BACKGROUND,
                    color: AXLE_TEAL,
                },
                '& .react-datepicker__day--selected.react-datepicker__day--highlighted': {
                    backgroundColor: AXLE_TEAL,
                    color: 'white',
                    fontWeight: 'bold',
                },
                '& .react-datepicker__day--highlighted:hover': {
                    backgroundColor: AXLE_TEAL,
                    color: 'white',
                },
                ...sx,
            }}
        >
            <FieldWithAxleLabel label={label} required={inputState.required}>
                <DatePicker
                    renderCustomHeader={({ date, decreaseMonth, increaseMonth }) => (
                        <AxleDatePickerHeader
                            date={date}
                            decreaseMonth={decreaseMonth}
                            increaseMonth={increaseMonth}
                        />
                    )}
                    // BEHAVIOR:
                    filterDate={filterDate}
                    highlightDates={highlightDates}
                    minDate={minSelectableDateTime}
                    maxDate={maxSelectableDateTime}
                    selected={inputState.value !== null ? new Date(inputState.value) : null}
                    onChange={(newDate) => {
                        if (newDate === null) {
                            if (nullable) {
                                inputState.setInputState(null);
                            } else {
                                Sentry.captureMessage(
                                    "Tried to set date to null, but it's not nullable.",
                                );
                            }
                            return;
                        }
                        inputState.setInputState(newDate);
                    }}
                    disabled={!enabled}
                    disabledKeyboardNavigation={disabledKeyboardNavigation}
                    // MOBILE LAYOUT:
                    withPortal={isMobileLayout}
                    // STYLING:
                    // The arrow often shows up in the wrong place, so just don’t show it.
                    showPopperArrow={false}
                    // This affects where the popper shows up if there is not enough space,
                    //  either above or below. And by forcing it to always show below, at least
                    //  the user can try to scroll down to reveal the UX that is cut off. Scrolling
                    //  up doesn’t seem to work as well.
                    popperPlacement='top-start'
                    // CUSTOM COMPONENT:
                    customInput={
                        // @ts-expect-error The remaining props are passed in by react-datepicker.
                        <AxleDateFieldCustomDisplayField
                            enabled={enabled}
                            nullable={nullable}
                            onClear={() => inputState.setInputState(null)}
                        />
                    }
                />
                <If condition={!hideHelperText}>
                    <FormHelperText error={inputState.showError}>
                        {inputState.helperText}
                    </FormHelperText>
                </If>
            </FieldWithAxleLabel>
        </Box>
    );
};

// Manage a whole datetime field.
export const AxleDateTimeField = ({
    label,
    enabled = true,
    inputState,
    nullable,
    timezone,
    intervalMinutes = 15,
}: {
    label: string;
    enabled?: boolean;
    inputState: InputState<Date | null>;
    nullable: boolean;
    timezone: string | null;
    intervalMinutes?: number;
}): ReturnComponent => {
    return (
        <FieldWithAxleLabel label={label} required={inputState.required}>
            <Box>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <AxleDateField
                            enabled={enabled}
                            hideHelperText={true}
                            inputState={inputState}
                            nullable={nullable}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <AxleDateTimeOfDayField
                            enabled={enabled}
                            hideHelperText={true}
                            inputState={inputState}
                            // We don't make the time of day nullable, because
                            //  1. there is already going to be an "X" button on the date field
                            //      to clear the date field
                            //  2. the default behaviour of clearing the time of day field is to
                            //      reset the field to its initial default value, which may not
                            //      be the same as null, and that's confusing.
                            nullable={false}
                            timezone={timezone}
                            intervalMinutes={intervalMinutes}
                        />
                    </Grid>
                </Grid>
            </Box>
            <FormHelperText error={inputState.showError}>{inputState.helperText}</FormHelperText>
        </FieldWithAxleLabel>
    );
};

// Manage a start and end datetime field.
export const AxleStartAndEndDateTimeField = ({
    enabled = true,
    startInputState,
    endInputState,
    timezone,
    nullable = false,
    shouldBeLessThan24Hours = false,
    intervalMinutes = 15,
}: {
    enabled?: boolean;
    startInputState: InputState<Moment | null>;
    endInputState: InputState<Moment | null>;
    timezone: string | null;
    nullable?: boolean;
    shouldBeLessThan24Hours?: boolean;
    intervalMinutes?: number;
}) => {
    const errorEndTimeBeforeStartTime = 'End time must be after start time';
    const errorLongerThan24Hours =
        'Duration cannot exceed 24 hours in length. Unless this is overnight, start date and end date should match.';

    const durationLongerThan24Hours = (moment1: Moment | null, moment2: Moment | null) => {
        if (!moment1 || !moment2) {
            return false;
        }
        return moment.duration(moment2.diff(moment1)).asHours() > 24;
    };

    useEffect(() => {
        // If the initial values are invalid, indicate an error.
        if (startInputState.value && endInputState.value) {
            if (endInputState.value <= startInputState.value) {
                // The input should still be marked as untouched,
                //  because the user has not touched the field yet.
                endInputState.setInputState(endInputState.value, {
                    error: errorEndTimeBeforeStartTime,
                    isTouched: false,
                });
            }
            if (
                shouldBeLessThan24Hours &&
                durationLongerThan24Hours(startInputState.value, endInputState.value)
            ) {
                endInputState.setInputState(endInputState.value, {
                    error: errorLongerThan24Hours,
                    isTouched: false,
                });
            }
        }
        // We only want to run this effect once on load, so ignoring this eslint rule.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // If the start date is not before the end date, make the end date the same
    //  as the start date and indicate an error.
    const newStartInputState: InputState<Moment | null> = {
        ...startInputState,
        setInputState: (newStartDateValueOrFunc) => {
            const newStartDate =
                typeof newStartDateValueOrFunc === 'function'
                    ? newStartDateValueOrFunc(startInputState.value)
                    : newStartDateValueOrFunc;

            if (newStartDate === null && !nullable) {
                Sentry.captureMessage('Attempted to set start date to null when nullable is false');
                return;
            }

            if (
                newStartDate !== null &&
                endInputState.value !== null &&
                newStartDate >= endInputState.value
            ) {
                startInputState.setInputState(newStartDate);
                endInputState.setInputState(newStartDate, { error: errorEndTimeBeforeStartTime });
                return;
            } else {
                startInputState.setInputState(newStartDate);
                endInputState.setInputState(endInputState.value); // This is to clear any error.
            }
            if (
                shouldBeLessThan24Hours &&
                durationLongerThan24Hours(newStartDate, endInputState.value)
            ) {
                endInputState.setInputState(endInputState.value, { error: errorLongerThan24Hours });
                return;
            }
        },
    };

    // If the end date is not after the start date, make the start date the same
    //  as the end date and indicate an error.
    const newEndInputState: InputState<Moment | null> = {
        ...endInputState,
        setInputState: (newEndDateValueOrFunc) => {
            const newEndDate =
                typeof newEndDateValueOrFunc === 'function'
                    ? newEndDateValueOrFunc(endInputState.value)
                    : newEndDateValueOrFunc;

            if (newEndDate === null && !nullable) {
                Sentry.captureMessage('Attempted to set end date to null when nullable is false');
                return;
            }

            if (
                newEndDate !== null &&
                startInputState.value !== null &&
                newEndDate <= startInputState.value
            ) {
                startInputState.setInputState(newEndDate);
                endInputState.setInputState(newEndDate, { error: errorEndTimeBeforeStartTime });
            } else {
                startInputState.setInputState(startInputState.value); // This is to clear any error.
                endInputState.setInputState(newEndDate);
            }

            if (
                shouldBeLessThan24Hours &&
                durationLongerThan24Hours(startInputState.value, newEndDate)
            ) {
                startInputState.setInputState(startInputState.value);
                endInputState.setInputState(newEndDate, { error: errorLongerThan24Hours });
                return;
            }
        },
    };

    return (
        <>
            <AxleMomentTimeField
                label={'Start Date'}
                enabled={enabled}
                inputState={newStartInputState}
                timezone={timezone}
                nullable={nullable}
                intervalMinutes={intervalMinutes}
            />
            <AxleMomentTimeField
                label={'End Date'}
                enabled={enabled}
                inputState={newEndInputState}
                timezone={timezone}
                nullable={nullable}
                intervalMinutes={intervalMinutes}
            />
        </>
    );
};

export const AxleDateCalendar = ({
    selectedDateState,
    calendarMonthViewState,
    datesWithAvailability = undefined,
    availabilityMetricsByDate = undefined,
    chosenDates = undefined,
    size = 'md',
    timezone,
}: {
    selectedDateState: InputState<Moment | null>;
    calendarMonthViewState: InputState<Moment>;
    datesWithAvailability?: Moment[];
    availabilityMetricsByDate?: VisitAvailabilityMetricsByDate;
    chosenDates?: Moment[];
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    timezone: string | null;
}) => {
    const hasAvailability = availabilityMetricsByDate?.availabilityByDate;
    // Don't allow selecting any date that's not in current month.
    const isDateInSelectableRange = (date: Date) => {
        const dateToSelect = moment(date);
        return !(
            dateToSelect.isBefore(calendarMonthViewState.value, 'month') ||
            dateToSelect.isAfter(calendarMonthViewState.value, 'month')
        );
    };

    const onMonthChange = (date: Date) => {
        const currentDate = moment(date);
        const startOfMonth = timezoneAdjustedMoment(currentDate.startOf('month'), timezone);
        calendarMonthViewState.setInputState(startOfMonth);
        selectedDateState.setInputState(null);
    };

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
            }}
        >
            <Typography variant='h3'>
                {selectedDateState.value?.format('dddd MMM. D') ?? 'Select a date'}
            </Typography>
            <Calendar
                ///////////////////////////////////////////////////////////////
                // Manage paging by month.
                ///////////////////////////////////////////////////////////////
                level={'month'}
                onPreviousMonth={onMonthChange}
                onNextMonth={onMonthChange}
                ///////////////////////////////////////////////////////////////
                // Manage selection of dates.
                ///////////////////////////////////////////////////////////////
                getDayProps={(date: Date) => ({
                    // We set the date to not be selected so that we can
                    //  specify super customized component based styles
                    //  in the renderDay function.
                    selected: false,
                    onClick: () => {
                        selectedDateState.setInputState(
                            timezoneAdjustedMoment(moment(date), timezone),
                        );
                    },
                })}
                excludeDate={(date: Date) => !isDateInSelectableRange(date)}
                ///////////////////////////////////////////////////////////////
                // Some misc props to configure the calendar.
                ///////////////////////////////////////////////////////////////
                minDate={new Date()}
                // This component makes the weekend days a different color, but we don't
                //  want that, so we just pass in an empty array.
                weekendDays={[]}
                firstDayOfWeek={0}
                ///////////////////////////////////////////////////////////////
                // Styling.
                ///////////////////////////////////////////////////////////////
                size={size}
                styles={{
                    day: {
                        borderRadius: 365,
                    },
                }}
                // Styling the day based on factors like whether it's currently
                //  selected, has availability, or has been chosen as part of a
                //  multi-date selection.
                renderDay={(javascriptDate: Date) => {
                    const date = moment(javascriptDate);

                    const doesDateHaveAvailability =
                        datesWithAvailability?.some((dateWithAvailability) =>
                            date.isSame(dateWithAvailability, 'day'),
                        ) ?? false;

                    const hasDayBeenChosen =
                        chosenDates?.some((chosenDate) => date.isSame(chosenDate, 'day')) ?? false;

                    const isDateCurrentlySelected = date.isSame(selectedDateState.value, 'day');

                    // We want to exclude all the dates that aren't inside of this calendar
                    //  month because it makes the calendar look weird if we don't make those
                    //  dates unselectable.
                    const isSelectableDate = isDateInSelectableRange(javascriptDate);

                    if (hasDayBeenChosen && isSelectableDate) {
                        return (
                            <DateItem
                                style={{
                                    backgroundColor: AXLE_TEAL,
                                    color: LIGHT_AXLE_TEAL_BACKGROUND,
                                    fontWeight: 700,
                                    border: isDateCurrentlySelected
                                        ? `2px solid ${DARK_AXLE_TEAL}`
                                        : undefined,
                                }}
                            >
                                {javascriptDate.getDate()}
                            </DateItem>
                        );
                    } else if (doesDateHaveAvailability && isSelectableDate) {
                        const availability =
                            availabilityMetricsByDate?.availabilityByDate[
                                date.format('YYYY-MM-DD')
                            ];

                        return (
                            <DateItem
                                style={{
                                    backgroundColor: hasAvailability
                                        ? undefined
                                        : LIGHT_AXLE_TEAL_BACKGROUND,
                                    borderRadius: hasAvailability ? undefined : 365,
                                    color: AXLE_TEAL,
                                    border: isDateCurrentlySelected
                                        ? `2px solid ${AXLE_TEAL}`
                                        : undefined,
                                    fontWeight: 700,
                                }}
                            >
                                {javascriptDate.getDate()}
                                {availability !== undefined && (
                                    <AvailabilityIndicator
                                        availability={availability}
                                        isSelected={isDateCurrentlySelected}
                                    />
                                )}
                            </DateItem>
                        );
                    } else if (isSelectableDate) {
                        return (
                            <DateItem
                                style={{
                                    border: isDateCurrentlySelected
                                        ? `2px solid ${AXLE_TEAL}`
                                        : undefined,
                                }}
                            >
                                {javascriptDate.getDate()}
                            </DateItem>
                        );
                    } else {
                        // We don't care to style any dates where it's just a normal
                        //  ol-date.
                        return javascriptDate.getDate();
                    }
                }}
            />
        </div>
    );
};
